import 'react-day-picker/style.css';

import { CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { format, isValid, parseISO } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker, DropdownProps } from 'react-day-picker';

import { cn } from '@/utils/classname';

interface StyledDatePickerProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  name: string;
  className?: string;
  filterDate?: (date: Date) => boolean;
}

export const StyledDatePicker: React.FC<StyledDatePickerProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  disabled = false,
  required = false,
  name,
  className = '',
  filterDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );
  const selected = useMemo(() => {
    if (value) {
      const parsed = parseISO(value);
      return isValid(parsed) ? parsed : undefined;
    }
    return undefined;
  }, [value]);

  // Parse min and max dates
  const minDate = min ? parseISO(min) : undefined;
  const maxDate = max ? parseISO(max) : undefined;

  // Format the date for display (MM/DD/YYYY)
  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = parseISO(dateStr);
    if (!isValid(date)) return '';
    return format(date, 'MM/dd/yyyy');
  };

  // Calculate dropdown position
  const calculatePosition = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 400; // Approximate height of calendar dropdown

    // If not enough space below but more space above, position on top
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  };

  // Handle date selection
  const handleSelect = (date: Date | undefined) => {
    if (date && isValid(date)) {
      onChange(format(date, 'yyyy-MM-dd'));
    } else {
      onChange('');
    }
    setIsOpen(false);
  };

  // Handle opening dropdown with position calculation
  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {label && (
        <label
          htmlFor={name}
          className="text-chariot-text-primary mb-2 block text-sm font-medium"
        >
          {label}
          {required && (
            <span className="text-chariot-stroke-critical ml-1">*</span>
          )}
        </label>
      )}

      {/* Date Display Field */}
      <div
        className={cn(
          'flex items-center justify-between gap-3',
          'bg-chariot-background-primary',
          'border-chariot-stroke rounded-lg border',
          'px-4 py-3',
          'cursor-pointer transition-all',
          'hover:border-chariot-brand-primary/50',
          isOpen &&
            'border-chariot-brand-primary ring-chariot-brand-primary/20 ring-1',
          disabled &&
            'hover:border-chariot-stroke cursor-not-allowed opacity-50'
        )}
        onClick={handleToggle}
      >
        <span
          className={cn(
            'text-sm',
            value ? 'text-chariot-text-primary' : 'text-chariot-text-tertiary'
          )}
        >
          {value ? formatDisplayDate(value) : 'Select date'}
        </span>
        <CalendarIcon className="text-chariot-text-secondary size-5" />
      </div>

      {/* Hidden input for form submission */}
      <input
        type="hidden"
        name={name}
        id={name}
        value={value || ''}
        required={required}
      />

      {/* Calendar Dropdown */}
      {isOpen && !disabled && (
        <div
          className={cn(
            'absolute z-50',
            dropdownPosition === 'bottom' ? 'mt-2' : 'bottom-full mb-2'
          )}
        >
          <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border p-3 shadow-lg">
            <style>{`
              .rdp-root {
                --rdp-accent-color: rgb(var(--chariot-brand-primary));
                --rdp-accent-background-color: rgb(var(--chariot-brand-primary) / 0.1);
                --rdp-selected: var(--rdp-accent-color);
                --rdp-selected-bg: var(--rdp-accent-color);
                --rdp-selected-text: white;
                --rdp-day-font: 500 0.875rem/1 system-ui;
                --rdp-caption-font: 600 0.875rem/1 system-ui;
              }
              .rdp-root .rdp-day {
                width: 2.5rem;
                height: 2.5rem;
                border-radius: 0.5rem;
                transition: all 0.2s;
              }
              .rdp-root .rdp-day:hover:not([aria-disabled="true"]):not([aria-selected="true"]) {
                background-color: rgb(var(--chariot-brand-primary) / 0.1);
                color: rgb(var(--chariot-brand-primary));
              }
              .rdp-root .rdp-selected .rdp-day_button {
                background-color: rgb(var(--chariot-brand-primary));
                color: white;
                font-weight: 600;
              }
              .rdp-root .rdp-disabled {
                opacity: 0.3;
                cursor: not-allowed;
              }
              .rdp-root .rdp-caption_label {
                font-weight: 600;
                color: var(--chariot-text-primary);
              }
              .rdp-root .rdp-chevron {
                width: 1.25rem;
                height: 1.25rem;
                color: var(--chariot-text-secondary);
              }
              .rdp-root .rdp-chevron:hover {
                color: var(--chariot-text-primary);
              }
              .rdp-root .rdp-month_grid {
                border-spacing: 0.25rem;
              }
              .rdp-root .rdp-weekday {
                color: var(--chariot-text-secondary);
                font-weight: 500;
                font-size: 0.75rem;
              }
              .rdp-root .rdp-outside {
                opacity: 0.4;
              }
              .rdp-root .rdp-today:not([aria-selected="true"]) .rdp-day_button {
                font-weight: 600;
                color: rgb(var(--chariot-brand-primary));
                position: relative;
              }
              .rdp-root .rdp-today:not([aria-selected="true"]) .rdp-day_button::after {
                content: '';
                position: absolute;
                bottom: 0.25rem;
                left: 50%;
                transform: translateX(-50%);
                width: 0.25rem;
                height: 0.25rem;
                border-radius: 50%;
                background-color: rgb(var(--chariot-brand-primary));
              }
            `}</style>
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={handleSelect}
              disabled={[
                ...(minDate && isValid(minDate) ? [{ before: minDate }] : []),
                ...(maxDate && isValid(maxDate) ? [{ after: maxDate }] : []),
                ...(filterDate ? [(date: Date) => !filterDate(date)] : []),
              ]}
              defaultMonth={selected || new Date()}
              showOutsideDays={false}
              className="rdp-root"
              captionLayout="dropdown"
              fromYear={1900}
              toYear={2100}
              components={{
                Dropdown: ({ value, onChange, options }: DropdownProps) => (
                  <div className="relative inline-block">
                    <select
                      value={value}
                      onChange={onChange}
                      className={cn(
                        'border-chariot-stroke appearance-none rounded-md border',
                        'bg-chariot-background-primary',
                        'px-2 py-1 pr-8 text-sm font-medium',
                        'text-chariot-text-primary',
                        'hover:border-chariot-brand-primary/50',
                        'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary/20 focus:ring-1 focus:outline-none',
                        'cursor-pointer transition-colors'
                      )}
                    >
                      {options?.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon className="text-chariot-text-secondary pointer-events-none absolute top-1/2 right-2 size-3 -translate-y-1/2" />
                  </div>
                ),
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
